import type { ReactNode } from 'react'
import type { components } from '../../../api/generated/schema'
import { Pentacle } from '../../../design/components'
import { formatRefresh, skillLadder } from '../types'
import {
  CAPACITY,
  LADDER,
  consequenceSlots,
  figure,
  gearGroups,
  gearTags,
  hungerTrack,
  mundaneTracks,
  padRows,
  signed,
  stressCircles,
  trackSkill,
} from './printModel'
import styles from './print.module.css'

type Schema = components['schemas']

/**
 * The printed sheet's ruled boxes.
 *
 * Every block here is a **form**: a fixed number of rows, the character's entries filling it
 * from the top, and blank rules below for the next few sessions. That is what fixes the page
 * count at one sheet for a character and two for a spellcaster, and it is why each block
 * takes a capacity rather than sizing itself to whatever the character owns today.
 *
 * They all read the section objects exactly as the API ships them — the same objects the
 * screen cards read — so there is no third representation of a character to keep in step.
 */

// ---------------------------------------------------------------------------
// Primitives
// ---------------------------------------------------------------------------

export function Block({
  title,
  grow = false,
  children,
}: {
  title: string
  /** Take the slack in the column, so the two columns end level. */
  grow?: boolean
  children: ReactNode
}) {
  return (
    <section className={`${styles.block} ${grow ? styles.blockGrow : ''}`}>
      <h2 className={styles.blockTitle}>{title}</h2>
      <div className={styles.blockBody}>{children}</div>
    </section>
  )
}

/**
 * One ruled line. With content it prints it; empty it prints the rule alone, which is what
 * the player writes the next entry on.
 */
export function Row({
  cost,
  children,
  trail,
}: {
  cost?: string | undefined
  children?: ReactNode
  trail?: string | undefined
}) {
  return (
    <div className={styles.row}>
      {cost !== undefined && <span className={styles.rowCost}>{cost}</span>}
      <span className={styles.rowMain}>{children}</span>
      {trail !== undefined && <span className={styles.rowTrail}>{trail}</span>}
    </div>
  )
}

/** A circle. Every slot on the sheet is one — easiest to fill with a pencil and cross out. */
export function Slot({ owned = true, marked = false }: { owned?: boolean; marked?: boolean }) {
  return (
    <span
      className={`${styles.slot} ${owned ? '' : styles.slotFuture} ${marked ? styles.slotDot : ''}`}
    />
  )
}

export function Bubble({ value, large = false }: { value: string; large?: boolean }) {
  return <span className={`${styles.bubble} ${large ? styles.bubbleLarge : ''}`}>{value}</span>
}

// ---------------------------------------------------------------------------
// Aspects
// ---------------------------------------------------------------------------

export interface AspectRow {
  key: string
  text: string
  temporary: boolean
}

export function AspectsBlock({
  highConcept,
  trouble,
  others,
}: {
  highConcept: string
  trouble: string
  others: AspectRow[]
}) {
  return (
    <Block title="Aspects">
      <p className={styles.aspectLabel}>High Concept</p>
      <div className={styles.aspectValue}>{highConcept}</div>

      <p className={styles.aspectLabel}>Trouble</p>
      <div className={styles.aspectValue}>{trouble}</div>

      <p className={styles.aspectLabel}>Other Aspects</p>
      {padRows(others, CAPACITY.otherAspects).map((aspect, index) => (
        <div
          key={aspect?.key ?? `blank-${index}`}
          className={`${styles.aspectValue} ${aspect?.temporary === true ? styles.temp : ''}`}
        >
          {aspect?.text ?? ''}
        </div>
      ))}
    </Block>
  )
}

// ---------------------------------------------------------------------------
// Skills
// ---------------------------------------------------------------------------

function ladderLabel(value: number): string {
  return LADDER.find((rung) => rung.value === value)?.label ?? ''
}

/**
 * The skill pyramid, printed as the official sheet prints it: every rung from the character's
 * cap down to Average, each with its slot count, whether or not anything sits on it. An empty
 * rung is a line to fill in — the pyramid is a shape the character is still growing into.
 */
export function SkillsBlock({
  skills,
  title = 'Skills',
  grow = false,
}: {
  skills: Schema['SkillsSectionDto'] | undefined
  title?: string
  grow?: boolean
}) {
  const rungs = skillLadder(skills)

  return (
    <Block title={title} grow={grow}>
      {rungs.map((rung) => (
        <div key={rung.levelValue} className={styles.rung}>
          <div className={styles.rungHead}>
            <span className={styles.rungName}>
              {rung.label === '' ? ladderLabel(rung.levelValue) : rung.label} (
              {signed(rung.levelValue)})
            </span>
            {/* A count only when the rung holds something. On the paper sheet this is a
                blank you fill in, and "# of slots: 0" reads as a claim rather than a space. */}
            <span className={styles.rungSlots}>
              # of slots:{rung.skills.length > 0 ? ` ${rung.skills.length}` : ''}
            </span>
          </div>
          <div className={styles.rungSkills}>
            {rung.skills.map((skill) => skill.name).join(', ')}
          </div>
        </div>
      ))}
    </Block>
  )
}

// ---------------------------------------------------------------------------
// Stress
// ---------------------------------------------------------------------------

function Track({ track }: { track: Schema['StressTrackDto'] }) {
  const name = String(track.type ?? '')
  const skill = trackSkill(name)

  return (
    <div className={styles.track}>
      <span className={styles.trackName}>
        <span className={styles.trackNameMain}>{name}</span>
        {skill !== undefined && <span className={styles.trackNameSkill}>({skill})</span>}
      </span>
      {stressCircles(track).map((circle) => (
        <Slot key={circle.value} owned={circle.owned} marked={circle.marked} />
      ))}
    </div>
  )
}

/**
 * The stress tracks, ten circles each.
 *
 * The official sheet prints eight and this prints ten, because a printed sheet outlives the
 * session it came off the printer in: the circles past the character's current track are
 * dashed, so an Endurance that goes up mid-campaign is a pencil mark rather than a reprint.
 */
export function StressBlock({
  stress,
  title = 'Stress',
}: {
  stress: Schema['StressSectionDto'] | undefined
  title?: string
}) {
  const tracks = mundaneTracks(stress)
  const hunger = hungerTrack(stress)

  return (
    <Block title={title}>
      <div className={styles.trackHead}>
        {Array.from({ length: 10 }, (_, index) => (
          <span key={index} className={styles.trackHeadCell}>
            {index + 1}
          </span>
        ))}
      </div>

      {tracks.map((track) => (
        <Track key={String(track.type)} track={track} />
      ))}

      {/* Hunger is not a fourth stress track — it is not cleared between scenes, and only
          feeding empties it. It prints inside the same box because that is where a reader
          looks for a track, and it carries the rule on the line beneath. */}
      {hunger !== undefined && <Track track={hunger} />}
      {hunger !== undefined && (
        <p className={styles.conNote}>Hunger clears only by feeding, never between scenes.</p>
      )}

      <div className={styles.armour}>
        <span className={styles.rowLead}>Armour, etc:</span>
        <span className={styles.armourRule} />
      </div>
    </Block>
  )
}

// ---------------------------------------------------------------------------
// Consequences
// ---------------------------------------------------------------------------

export function ConsequencesBlock({
  consequences,
}: {
  consequences: Schema['ConsequencesSectionDto'] | undefined
}) {
  const slots = consequenceSlots(consequences)

  return (
    <Block title="Consequences">
      <div className={styles.conHead}>
        <span className={styles.conType}>Type</span>
        <span className={styles.conScope}>P/M/S</span>
        <span className={styles.conPenalty}>Stress</span>
        <span className={styles.conUsed}>Used?</span>
        <span className={styles.conAspect}>Aspect</span>
      </div>

      {padRows(slots, slots.length + CAPACITY.spareConsequences).map((slot, index) => (
        <div key={slot?.id ?? `blank-${index}`} className={styles.conRow}>
          <span className={styles.conType}>{slot === null ? '' : String(slot.level ?? '')}</span>
          <span className={styles.conScope}>{slot === null ? '' : String(slot.type ?? 'Any')}</span>
          <span className={styles.conPenalty}>
            {slot === null ? '' : signed(slot.penalty ?? 0)}
          </span>
          {/* Solid even on the spare rows, as the official sheet prints its four. Unlike a
              stress track, whose length is a number this app knows, a spare consequence row
              is just a line to write on — dashing it says something about the character that
              a blank sheet has no business saying. */}
          <span className={styles.conUsed}>
            <Slot marked={slot?.isOccupied === true} />
          </span>
          <span className={styles.conAspect}>
            {slot?.consequence?.name ?? ''}
            {(slot?.consequence?.effect ?? '') !== '' && (
              <span className={styles.conNote}> — {slot?.consequence?.effect}</span>
            )}
          </span>
        </div>
      ))}
    </Block>
  )
}

// ---------------------------------------------------------------------------
// Stunts and powers
// ---------------------------------------------------------------------------

/**
 * Stunts and powers as the official sheet lists them — cost, then name.
 *
 * Descriptions are deliberately not printed. They are what pushed the sheet onto a third
 * page, and a table that needs the exact wording of Evocation has the book or the app open;
 * what a printed sheet is for is remembering *which* powers a character has and what each
 * one cost them.
 */
export function AbilitiesBlock({
  abilities,
  shapeId,
  capacity = CAPACITY.abilities,
  grow = false,
}: {
  abilities: Schema['AbilitiesSectionDto'] | undefined
  shapeId: number | null
  capacity?: number
  grow?: boolean
}) {
  const entries = (abilities?.abilities ?? [])
    .filter((ability) => {
      const ids = ability.shapeIds ?? []
      return ids.length === 0 || shapeId === null || ids.includes(shapeId)
    })
    // Powers before stunts, as the sheet reads, then alphabetical inside each.
    .sort((a, b) => {
      if (a.type !== b.type) return a.type === 'Power' ? -1 : 1
      return (a.name ?? '').localeCompare(b.name ?? '')
    })

  return (
    <Block title="Stunts &amp; Powers" grow={grow}>
      <div className={styles.colHeads}>
        <span className={styles.rowCost}>Cost</span>
        <span className={styles.rowMain}>Ability</span>
      </div>

      {/* Set larger than the rest of the sheet: this is the box a player reads mid-scene, at
          arm's length, to remember what their character can actually do. */}
      <div className={styles.abilityRows}>
        {padRows(entries, capacity).map((ability, index) => (
          <Row
            key={ability?.id ?? `blank-${index}`}
            cost={ability === null ? '' : formatRefresh(ability.refreshCost ?? 0)}
          >
            {ability !== null && <span className={styles.rowName}>{ability.name}</span>}
            {ability?.type === 'Stunt' && <span className={styles.rowNote}> · stunt</span>}
          </Row>
        ))}
      </div>
    </Block>
  )
}

// ---------------------------------------------------------------------------
// Gear
// ---------------------------------------------------------------------------

/**
 * Gear &amp; Items — the block the official sheets do not have.
 *
 * On paper a character's weapons and armour go in the single "Armor, Etc:" line inside the
 * stress box, and everything else ends up on the back of the page. The app tracks them as
 * real entries with ratings and an equipped flag, so the printed sheet gives them a box.
 */
export function GearBlock({
  equipment,
  capacity = CAPACITY.gear,
  grow = false,
}: {
  equipment: Schema['EquipmentSectionDto'] | undefined
  capacity?: number
  grow?: boolean
}) {
  const items = gearGroups(equipment).flatMap((group) => group.items)

  return (
    <Block title="Gear &amp; Items" grow={grow}>
      <div className={styles.colHeads}>
        <span className={styles.rowMain}>Item</span>
        <span className={styles.rowTrail}>Rating</span>
      </div>

      {padRows(items, capacity).map((item, index) => (
        <Row
          key={item?.id ?? `blank-${index}`}
          {...(item === null ? {} : { trail: gearTags(item).join(' · ') })}
        >
          {item !== null && <span className={styles.rowName}>{item.name}</span>}
        </Row>
      ))}
    </Block>
  )
}

// ---------------------------------------------------------------------------
// Fate, refresh and the ladder
// ---------------------------------------------------------------------------

/**
 * The sheet's bottom-left corner: the budgets a character is built from, and the fate pool.
 *
 * **Only the fixed figures print.** Refresh and the skill-point total are what a character
 * *may* spend — they hold still for whole arcs — so the sheet states them. Fate points in
 * hand and skill points spent move during play, so their circles print empty: a printed
 * number that was true when the sheet left the printer is worse than a blank, because the
 * table has to work out whether to believe it.
 */
export function FateBlock({
  fate,
  skills,
  poolLabel,
  skillCap,
  grow = false,
}: {
  fate: { refreshLevel: number } | undefined
  skills: Schema['SkillsSectionDto'] | undefined
  /** Already resolved by the caller — empty on a blank sheet, which knows neither. */
  poolLabel: string
  skillCap: string
  grow?: boolean
}) {
  return (
    <Block title="Fate &amp; Refresh" grow={grow}>
      <div className={styles.fatePool}>
        <Pentacle size={64} className={styles.pentacle} />
        <span className={styles.fatePoolLabel}>
          Current
          <br />
          Fate Points
        </span>
        <Bubble value="" large />
      </div>

      <div className={styles.fateGrid}>
        <div className={styles.fateRow}>
          <span className={styles.fateLabel}>Base refresh level</span>
          <Bubble value={figure(fate?.refreshLevel)} />
        </div>
        {/* Blank: adjusted refresh moves whenever a power is bought or bought back, and it is
            the number a player recalculates in front of the Game Master. */}
        <div className={styles.fateRow}>
          <span className={styles.fateLabel}>Adjusted refresh</span>
          <Bubble value="" />
        </div>
        <div className={styles.fateRow}>
          <span className={styles.fateLabel}>Skill points spent</span>
          <Bubble value="" />
        </div>
        <div className={styles.fateRow}>
          <span className={styles.fateLabel}>Total available</span>
          <Bubble value={figure(skills?.totalPoints)} />
        </div>
      </div>

      <div className={styles.footNote}>
        <span>Power level: {poolLabel}</span>
        <span>Skill cap: {skillCap}</span>
      </div>
    </Block>
  )
}

/**
 * The Fate ladder, printed as a key the way every official sheet prints it. It belongs to the
 * system rather than to the character: a reader converting "Superb" to +5 mid-roll is the
 * reason it is on the paper at all.
 */
export function LadderBlock() {
  return (
    <Block title="The Ladder">
      <ul className={styles.ladder}>
        {LADDER.map((rung) => (
          <li key={rung.value} className={styles.ladderRow}>
            <span>{rung.label}</span>
            <span className={styles.ladderValue}>{signed(rung.value)}</span>
          </li>
        ))}
      </ul>
    </Block>
  )
}
